import PoolCoordinator from "./PoolCoordinator";
import Game from "../Game";

interface PoolHealthReport {
  healthy: boolean;
  issues: string[];
  stats: {
    audio: { active: number; total: number; utilization: number };
    vfx: { active: number; total: number; utilization: number };
    tween: { active: number; total: number; utilization: number };
  };
}

/**
 * Pooling Facade with Real Coordination Logic
 * Single Responsibility: Coordinate cross-pool operations, resource management, and performance optimization
 *
 * This facade provides meaningful value by:
 * - Coordinating operations across multiple pools
 * - Monitoring and managing pool resources
 * - Optimizing performance based on conditions
 * - Handling emergency cleanup scenarios
 */
export default class PoolingFacade {
  private coordinator: PoolCoordinator;
  private scene: Phaser.Scene;
  private performanceLevel: "low" | "medium" | "high" = "high";
  private lastHealthCheck: number = 0;
  private healthCheckInterval: number = 5000; // 5 seconds

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.coordinator = new PoolCoordinator(scene);
  }

  // ===== COORDINATED GAME OPERATIONS =====

  /**
   * Complete enemy death with coordinated audio and VFX
   * Automatically checks sound settings and falls back to VFX-only when sounds disabled
   */
  showEnemyDeathWithAudio(x: number, y: number, scale: number = 3): void {
    // Check sound settings
    if (Game.Core.PlayerOptions.SoundsEnabled !== false) {
      // Sounds enabled: check performance and adapt
      if (this.performanceLevel === "low") {
        // Low performance: audio only, no VFX
        this.coordinator.audioPool.playSound("kill_sound", { volume: 0.15 });
      } else {
        // Normal/High performance: both audio and VFX
        this.coordinator.audioPool.playSound("kill_sound", { volume: 0.15 });
        this.coordinator.vfxPool.showDeathAnimation(x, y, scale);
      }
    } else {
      // Sounds disabled: VFX only (if performance allows)
      if (this.performanceLevel !== "low") {
        this.coordinator.vfxPool.showDeathAnimation(x, y, scale);
      }
    }
  }

  /**
   * Play audio with automatic resource management
   * Automatically checks sound settings and skips audio when disabled
   */
  async playSound(
    soundKey: string,
    config: { volume?: number; loop?: boolean } = {}
  ): Promise<void> {
    // Check sound settings first
    if (Game.Core.PlayerOptions.SoundsEnabled === false) {
      return; // Skip audio when sounds are disabled
    }

    // Auto-optimize if pools are stressed
    this.autoOptimizeIfNeeded();

    // Determine volume based on performance level
    const adjustedVolume = this.adjustVolumeForPerformance(
      config.volume || 0.5
    );

    return this.coordinator.audioPool.playSound(soundKey, {
      ...config,
      volume: adjustedVolume,
    });
  }

  /**
   * Create animation with resource monitoring
   */
  animateDamageNumber(
    target: any,
    startY: number,
    damage: number,
    onComplete?: () => void
  ): void {
    const targetId = target.damageNumberId || "unknown";
    console.log(
      `[PoolingFacade] ${targetId} - animateDamageNumber called, performanceLevel: ${this.performanceLevel}, startY: ${startY}`
    );

    // Skip complex animations on low performance
    if (this.performanceLevel === "low") {
      console.log(
        `[PoolingFacade] ${targetId} - Skipping animation due to low performance, calling onComplete immediately`
      );
      if (onComplete) {
        try {
          onComplete();
          console.log(
            `[PoolingFacade] ${targetId} - onComplete callback executed successfully (low performance path)`
          );
        } catch (error) {
          console.error(
            `[PoolingFacade] ${targetId} - Error in onComplete callback (low performance path):`,
            error
          );
        }
      }
      return;
    }

    // Validate target before creating tweens
    if (!this.isValidAnimationTarget(target)) {
      console.warn(
        `[PoolingFacade] ${targetId} - Invalid animation target, calling onComplete immediately`
      );
      if (onComplete) {
        try {
          onComplete();
          console.log(
            `[PoolingFacade] ${targetId} - onComplete callback executed successfully (invalid target path)`
          );
        } catch (error) {
          console.error(
            `[PoolingFacade] ${targetId} - Error in onComplete callback (invalid target path):`,
            error
          );
        }
      }
      return;
    }

    console.log(
      `[PoolingFacade] ${targetId} - Target validation passed, running auto-optimization check`
    );
    this.autoOptimizeIfNeeded();

    // Create both tweens at once to avoid nested callbacks
    console.log(
      `[PoolingFacade] ${targetId} - Creating damage number animation`
    );

    // Store reference to ensure callback is called
    let animationCompleted = false;
    const ensureCompletion = () => {
      if (!animationCompleted) {
        animationCompleted = true;
        console.log(
          `[PoolingFacade] ${targetId} - Ensuring animation completion`
        );
        if (onComplete) {
          try {
            onComplete();
          } catch (error) {
            console.error(
              `[PoolingFacade] ${targetId} - Error in onComplete:`,
              error
            );
          }
        }
      }
    };

    // Set initial scale for the animation (relative to current scale)
    const currentScale = target.scale || 1;
    const animatedScale = currentScale * 2;

    try {
      target.setScale(animatedScale);
    } catch (error) {
      console.error(
        `[PoolingFacade] ${targetId} - Error setting initial scale:`,
        error
      );
    }

    // Calculate duration based on current number of active damage numbers
    // More damage numbers = longer duration to keep them visible
    const tweenStats = this.coordinator.tweenPool.getStats();
    const activeDamageNumbers = tweenStats.activeInstances;

    // Get the current damage number count from the game
    let currentDamageNumbers = 0;
    try {
      if (Game && Game.Core && Game.Core.damageNumberPool) {
        const poolStats = Game.Core.damageNumberPool.getStats();
        currentDamageNumbers = poolStats.spawned;
      }
    } catch (error) {
      // Fallback to tween count
      currentDamageNumbers = activeDamageNumbers;
    }

    // Dynamic duration calculation:
    // - Base: 800ms (increased from 600ms)
    // - Add 100ms for every 3 concurrent damage numbers
    // - Cap at 2000ms for explosions with many hits
    const baseDuration = 800;
    const additionalDuration = Math.floor(currentDamageNumbers / 3) * 100;
    const duration = Math.min(baseDuration + additionalDuration, 2000);

    // Also adjust the Y distance based on number of damage numbers
    // More numbers = travel further to avoid overlap
    const baseYDistance = 20;
    const additionalYDistance = Math.min(currentDamageNumbers * 2, 40);
    const totalYDistance = baseYDistance + additionalYDistance;

    console.log(
      `[PoolingFacade] ${targetId} - Active damage numbers: ${currentDamageNumbers}, duration: ${duration}ms, Y distance: ${totalYDistance}`
    );

    // Add a small random delay to stagger the animations when many appear at once
    const staggerDelay = currentDamageNumbers > 5 ? Math.random() * 200 : 0;

    // Create a simple combined tween instead of nested tweens
    const combinedTween = this.coordinator.tweenPool.createDamageNumberTween({
      targets: target,
      y: startY - totalYDistance,
      scale: currentScale, // Return to original scale
      alpha: 0,
      duration: duration,
      delay: staggerDelay,
      ease: "Cubic.easeOut",
      onComplete: ensureCompletion,
    });

    // Fallback if pooled tween fails
    if (!combinedTween) {
      console.warn(
        `[PoolingFacade] ${targetId} - Tween creation failed, using fallback`
      );
      // Create a simple fallback tween
      this.createFallbackTween(target, {
        y: startY - totalYDistance,
        scale: currentScale, // Return to original scale
        alpha: 0,
        duration: duration,
        ease: "Cubic.easeOut",
        onComplete: ensureCompletion,
      });
    } else {
      console.log(
        `[PoolingFacade] ${targetId} - Combined tween created successfully`
      );
    }
  }

  // ===== CONVENIENCE METHODS (with coordination) =====

  async playKillSound(): Promise<void> {
    return this.playSound("kill_sound", { volume: 0.15 });
  }

  async playCoinSound(): Promise<void> {
    return this.playSound("coin_sound", { volume: 0.4 });
  }

  async playAppleSound(): Promise<void> {
    return this.playSound("apple_sound", { volume: 0.8 });
  }

  async playExplodeSound(): Promise<void> {
    return this.playSound("explode_sound", { volume: 0.7 });
  }

  async playTreasureMusic(): Promise<void> {
    if (Game.Core?.PlayerOptions?.MusicEnabled === false) return;
    return this.playSound("treasure_music", { volume: 0.7, loop: false });
  }

  async playLevelUpMusic(): Promise<void> {
    if (Game.Core?.PlayerOptions?.MusicEnabled === false) return;
    return this.playSound("levelup_music", { volume: 0.5, loop: false });
  }

  async playGameOverSound(): Promise<void> {
    return this.playSound("gameover_sound", { volume: 0.5, loop: false });
  }

  showEnemyDeath(x: number, y: number, scale: number = 3): void {
    // Skip VFX on low performance
    if (this.performanceLevel === "low") return;

    this.autoOptimizeIfNeeded();
    this.coordinator.vfxPool.showDeathAnimation(x, y, scale);
  }

  // ===== VISUAL EFFECTS =====

  /**
   * Flash target with damage effect (red tint)
   */
  showDamageFlash(
    target: Phaser.GameObjects.GameObject,
    duration: number = 120
  ): void {
    if (this.performanceLevel === "low") return;
    this.autoOptimizeIfNeeded();
    this.coordinator.vfxPool.showDamageFlash(target, duration);
  }

  // ===== RESOURCE MANAGEMENT & OPTIMIZATION =====

  /**
   * Get comprehensive pool health report
   */
  getPoolHealth(): PoolHealthReport {
    const audioStats = this.coordinator.audioPool.getStats();
    const vfxStats = this.coordinator.vfxPool.getStats();
    const tweenStats = this.coordinator.tweenPool.getStats();

    const issues: string[] = [];

    // Check utilization rates
    const audioUtilization =
      audioStats.activeInstances / audioStats.totalInstances;
    const vfxUtilization = vfxStats.activeInstances / vfxStats.totalInstances;
    const tweenUtilization =
      tweenStats.activeInstances / tweenStats.totalInstances;

    if (audioUtilization > 0.8) issues.push("Audio pool near capacity");
    if (vfxUtilization > 0.8) issues.push("VFX pool near capacity");
    if (tweenUtilization > 0.8) issues.push("Tween pool near capacity");

    return {
      healthy: issues.length === 0,
      issues,
      stats: {
        audio: {
          active: audioStats.activeInstances,
          total: audioStats.totalInstances,
          utilization: audioUtilization,
        },
        vfx: {
          active: vfxStats.activeInstances,
          total: vfxStats.totalInstances,
          utilization: vfxUtilization,
        },
        tween: {
          active: tweenStats.activeInstances,
          total: tweenStats.totalInstances,
          utilization: tweenUtilization,
        },
      },
    };
  }

  /**
   * Set performance level and optimize accordingly
   */
  setPerformanceLevel(level: "low" | "medium" | "high"): void {
    this.performanceLevel = level;
    this.optimizeForPerformance();
  }

  /**
   * Optimize pools based on current performance level
   */
  private optimizeForPerformance(): void {
    switch (this.performanceLevel) {
      case "low":
        // Reduce pool sizes and limits
        (this.coordinator.audioPool as any).totalMaxInstances = 10;
        (this.coordinator.vfxPool as any).totalMaxInstances = 15;
        (this.coordinator.tweenPool as any).totalMaxInstances = 30;
        break;
      case "medium":
        (this.coordinator.audioPool as any).totalMaxInstances = 15;
        (this.coordinator.vfxPool as any).totalMaxInstances = 30;
        (this.coordinator.tweenPool as any).totalMaxInstances = 60;
        break;
      case "high":
        (this.coordinator.audioPool as any).totalMaxInstances = 20;
        (this.coordinator.vfxPool as any).totalMaxInstances = 50;
        (this.coordinator.tweenPool as any).totalMaxInstances = 100;
        break;
    }
  }

  /**
   * Automatic optimization when resources are stressed
   */
  private autoOptimizeIfNeeded(): void {
    const now = Date.now();
    if (now - this.lastHealthCheck < this.healthCheckInterval) return;

    this.lastHealthCheck = now;
    const health = this.getPoolHealth();

    if (!health.healthy) {
      // Emergency optimization
      this.emergencyCleanup();

      // Temporarily reduce performance level if severely stressed
      if (health.issues.length >= 2 && this.performanceLevel === "high") {
        this.setPerformanceLevel("medium");
      } else if (
        health.issues.length >= 3 &&
        this.performanceLevel === "medium"
      ) {
        this.setPerformanceLevel("low");
      }
    }
  }

  /**
   * Emergency cleanup when pools are stressed
   */
  private emergencyCleanup(): void {
    // Stop oldest audio instances
    this.coordinator.audioPool.stopAllSounds();

    // Clear oldest VFX instances
    this.coordinator.vfxPool.stopAllVFX();

    // Kill all non-essential tweens
    this.coordinator.tweenPool.killAllTweens();
  }

  /**
   * Adjust volume based on performance level
   */
  private adjustVolumeForPerformance(baseVolume: number): number {
    switch (this.performanceLevel) {
      case "low":
        return baseVolume * 0.7; // Reduce volume to save resources
      case "medium":
        return baseVolume * 0.85;
      case "high":
        return baseVolume;
      default:
        return baseVolume;
    }
  }

  // ===== VALIDATION & UTILITY METHODS =====

  private isValidAnimationTarget(target: any): boolean {
    if (!target || typeof target !== "object" || target === null) {
      return false;
    }

    // Check if target is a valid Phaser game object
    if (target.scene && !target.scene.sys) {
      return false;
    }

    return true;
  }

  private createFallbackTween(target: any, config: any): void {
    const targetId = target.damageNumberId || "unknown";
    console.log(
      `[PoolingFacade] ${targetId} - Creating fallback tween with config:`,
      {
        duration: config.duration,
        scale: config.scale,
        y: config.y,
        alpha: config.alpha,
      }
    );

    try {
      const tween = this.scene.tweens.add({
        targets: target,
        ...config,
      });
      console.log(
        `[PoolingFacade] ${targetId} - Fallback tween created successfully`
      );

      // Add error handling to the tween itself
      if (tween) {
        tween.on("error", (error: any) => {
          console.error(
            `[PoolingFacade] ${targetId} - Fallback tween error:`,
            error
          );
          if (config.onComplete) {
            config.onComplete();
          }
        });
      }
    } catch (error) {
      console.error(
        `[PoolingFacade] ${targetId} - Failed to create fallback tween:`,
        error
      );
      // Silently fail and ensure callback is called
      if (config.onComplete) {
        console.log(
          `[PoolingFacade] ${targetId} - Calling onComplete due to fallback tween creation failure`
        );
        try {
          config.onComplete();
          console.log(
            `[PoolingFacade] ${targetId} - onComplete called successfully after fallback failure`
          );
        } catch (callbackError) {
          console.error(
            `[PoolingFacade] ${targetId} - Error in onComplete callback after fallback failure:`,
            callbackError
          );
        }
      }
    }
  }

  // ===== LIFECYCLE =====

  destroy(): void {
    this.coordinator.destroy();
  }
}
